import { useState, useMemo } from "react";
import { useDates } from "dates/DatesContext";
import { useFetch } from "hooks/api";
import useSelectedMetrics from "components/custom/merchandise/hooks/useSelectedMetrics";
import MultiMetricChart from "components/custom/merchandise/MultiMetricChart";
import Loading from "components/core/blocks/Loading";
import { buildMetric, getFormatter } from "views/merchandise/metrics";
import DetailedMetric from "components/core/blocks/DetailedMetric";
import { useAcosOrRoas } from "utils/acosOrRoas";
import { formatCurrency } from "utils/formatNumber";
import moment from "moment";
import { Row, Col, Nav } from "react-bootstrap";
import { useModuleEnabled } from "redux/selectors/modules";
import BiddingProgramLog from "components/custom/programs/bidding/BiddingProgramLog";
import { useAppSelector } from "redux/hooks";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { trellisPalette } from "components/custom/analytics/palettes";
import Accordion from "components/core/basic/Accordion";
import ExpanderContainer from "components/core/basic/ExpanderContainer";
import { keepPreviousData } from "@tanstack/react-query";

const ImpressionCurve = ({ data }) => {
  const options = {
    title: { text: "" },
    colors: trellisPalette,
    chart: {
      type: "line",
      style: {
        fontFamily: "Poppins",
        fontWeight: 300,
        fontSize: "15px",
      },
    },
    xAxis: {
      categories: data.map((d) => d.bin_mid),
    },
    yAxis: [
      {
        title: {
          text: "Impressions",
        },
      },
      {
        title: {
          text: "Profitability",
        },
        opposite: true,
      },
    ],
    series: [
      {
        name: "Effective Impressions",
        data: data.map((d) => d.eff_impressions),
      },
      {
        name: "Impression Rate",
        data: data.map((d) => d.imp_rate),
      },
      {
        name: "Smooth Impression Rate",
        data: data.map((d) => d.smooth_imp_rate),
      },
      {
        name: "Smooth Impressions",
        data: data.map((d) => d.smooth_impressions),
      },
      {
        name: "Profitablity",
        data: data.map((d) => d.multiplier_curve),
        yAxis: 1,
      },
      {
        name: "Profit Curve",
        data: data.map((d) => d.modeled_profit_curve),
        yAxis: 1,
      },
    ],
  };
  return (
    <div>
      <h5 className="text-center">Impression Curve</h5>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};

const TargetDetails = ({
  row,
  targetType,
  marketPlace = { marketPlace: "USA" },
  channel = "walmart",
  formatTargetText,
}) => {
  const [tab, setTab] = useState("overview");

  const isStaff = useAppSelector((state) => state.user?.is_staff ?? false);
  const debugModule = useModuleEnabled("DEBUG");
  const canViewDebug = isStaff && debugModule;

  const acosOrRoas = useAcosOrRoas();

  const { text, target_id, match_type, refinements } = row._original ?? {};

  const { group, start, end, preStart, preEnd } = useDates();

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    empty: true,
    target_id: target_id,
    target_type: targetType,
    kw_report: false,
    channel: channel,
  };

  const { data: targetData, isLoading } = useFetch(
    [
      "keyword_report",
      filters.start_date,
      filters.end_date,
      filters.pre_start_date,
      filters.pre_end_date,
      group,
      target_id,
    ],
    `/api/data_report/keyword_report`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      enabled: !!target_id,
      staleTime: 2 * 60 * 1000,
    },
  );

  const { data: targetBidData } = useFetch(["target_details", target_id], "/target_details", filters, {
    select: (d) => d.data,
    placeholderData: keepPreviousData,
    enabled: !!target_id,
    staleTime: 2 * 60 * 1000,
  });

  const bidhistoryFilters = { limit: 1 };
  if (targetType === "keyword") {
    bidhistoryFilters["adv_keyword"] = target_id;
  } else {
    bidhistoryFilters["adv_target"] = target_id;
  }
  const { data: bidhistory } = useFetch(["bidding", targetType, target_id], "/bidhistory", bidhistoryFilters, {
    select: (d) => d.data?.results?.[0],
    enabled: !!target_id && canViewDebug,
  });

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(["ad_sales", "clicks", "daily_bid", "impressions"]);

  const metrics = useMemo(
    () => [
      buildMetric(marketPlace, targetData, "clicks", "clicks_percent"),
      buildMetric(marketPlace, targetData, "ad_spend", "ad_spend_percent"),
      buildMetric(marketPlace, targetData, "orders", "orders_percent"),
      buildMetric(marketPlace, targetData, "daily_bid", null),
      buildMetric(marketPlace, targetData, "impressions", "impressions_percent"),
      buildMetric(marketPlace, targetData, "ad_sales", "ad_sales_percent"),
      acosOrRoas(
        buildMetric(marketPlace, targetData, "acos", "acos_delta"),
        buildMetric(marketPlace, targetData, "roas", "roas_delta"),
      ),
      buildMetric(marketPlace, targetData, "ctr", "ctr_delta"),
      buildMetric(marketPlace, targetData, "conversion_rate", "conversion_rate_delta"),
      buildMetric(marketPlace, targetData, "cpc", "cpc_delta"),
    ],
    [acosOrRoas, marketPlace, targetData],
  );

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  const dateFilterWithoutToday = targetData?.date?.filter((d) => moment().format("YYYY-MM-DD") !== d.start_date) ?? [];

  const currencyFormatter = getFormatter(marketPlace.marketPlace, "currency");
  const percentFromatter = getFormatter(marketPlace.marketPlace, "percent");

  // details exclude exploitation_curves
  const details = bidhistory ? { ...bidhistory } : {};
  details.exploitation_curves = undefined;

  if (isLoading) {
    return (
      <ExpanderContainer>
        <Loading height={"300px"} fullPage={false} />
      </ExpanderContainer>
    );
  }

  return (
    <ExpanderContainer>
      <Row className="pt-3">
        <Col xs={12}>
          <h3
            className="pb-4 px-5"
            style={{
              fontSize: 22,
            }}
          >
            {formatTargetText(text, { ...refinements, marketPlace: marketPlace.marketPlace })} - {match_type}
          </h3>
        </Col>
        <Col xs={12} className="mt-5 ph-4 mb-3 border-bottom fs-standard">
          <Nav
            variant="underline"
            onSelect={(k) => {
              return setTab(k);
            }}
            activeKey={tab}
          >
            <Nav.Item>
              <Nav.Link eventKey="overview">Overview</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="logs">Bidding Rules History</Nav.Link>
            </Nav.Item>
            {canViewDebug && (
              <Nav.Item>
                <Nav.Link eventKey="debug">Bidding Parameters</Nav.Link>
              </Nav.Item>
            )}
          </Nav>
        </Col>

        {tab === "overview" && (
          <>
            <Col xs={12}>
              <MultiMetricChart
                metrics={metrics}
                selectedMetrics={selectedMetrics}
                data={dateFilterWithoutToday}
                group={group}
                handleLegendChange={handleLegendChange}
                datesFormatted={true}
                showAnnotations={false}
              />
            </Col>
            <Col xs={12}>
              <Row className="pt-4">
                {metrics
                  ?.filter((m) => !["daily_bid", "ctr", "conversion_rate", "cpc"].includes(m.id))
                  ?.map((m, i) => {
                    return (
                      <Col xs={12} md={6} lg={2} key={m.id} className={`mb-4 ${i % 2 === 0 ? "ps-2" : "pe-2"}`}>
                        <div className="">
                          <div>
                            <DetailedMetric
                              center
                              size={"small"}
                              id={m.id}
                              toolTip={m.toolTip}
                              name={m.name}
                              value={m.overrideValue ?? m.actual}
                              target={m.target}
                              formatter={m.formatter}
                              targetFormatter={m?.targetFormatter}
                              showTarget={false}
                              targetLabel={m?.targetLabel}
                              targetColor={m.target > 0 ? "#007000" : "#d2222d"}
                            />
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                {targetBidData?.manual_bid && (
                  <Col xs={12} md={6} lg={2} className={`mb-4 pe-2`}>
                    <div className="">
                      <div>
                        <DetailedMetric
                          center
                          size={"small"}
                          id={"manual_bid"}
                          name={"Fixed Bid"}
                          value={targetBidData?.manual_bid}
                          formatter={(v) => formatCurrency(v, marketPlace.marketPlace, false, false, true)}
                          showTarget={false}
                        />
                      </div>
                    </div>
                  </Col>
                )}
                {targetBidData?.floor_bid && (
                  <Col xs={12} md={6} lg={2} className={`mb-4 pe-2`}>
                    <div>
                      <div>
                        <DetailedMetric
                          center
                          size={"small"}
                          id={"floor_bid"}
                          name={"Minimum Bid"}
                          value={targetBidData?.floor_bid}
                          formatter={(v) => formatCurrency(v, marketPlace.marketPlace, false, false, true)}
                          showTarget={false}
                        />
                      </div>
                    </div>
                  </Col>
                )}
                {targetBidData?.ceil_bid && (
                  <Col xs={12} md={6} lg={2} className={`mb-4 pe-2`}>
                    <div>
                      <div>
                        <DetailedMetric
                          center
                          size={"small"}
                          id={"ceil_bid"}
                          name={"Maximum Bid"}
                          value={targetBidData?.ceil_bid}
                          formatter={(v) => formatCurrency(v, marketPlace.marketPlace, false, false, true)}
                          showTarget={false}
                        />
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </Col>
          </>
        )}
        {tab === "logs" && (
          <Col xs={12} style={{ minHeight: "250px" }} className="px-5">
            <Row>
              {/* targetId => adv_keyword or adv_target */}
              <BiddingProgramLog targetId={target_id} targetType={targetType} marketPlace={marketPlace} />
            </Row>
          </Col>
        )}
        {tab === "debug" && (
          <Col xs={12} className="fs-standard px-5">
            <Row>
              <Col xs={4} className="pt-3">
                <h5 className="text-center">Bid Details</h5>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <th>Current Bid</th>
                      <td>{currencyFormatter(bidhistory?.new_bid)}</td>
                    </tr>
                    <tr>
                      <th>Bidding Stage</th>
                      <td>{bidhistory?.history?.bid_stage}</td>
                    </tr>
                    {bidhistory?.history?.bid_stage?.startsWith("v1") && (
                      <>
                        <tr>
                          <th>ACOS Bid</th>
                          <td>{currencyFormatter(bidhistory?.history?.acos_bid)}</td>
                        </tr>
                        <tr>
                          <th>Effective CR</th>
                          <td>{percentFromatter(bidhistory?.history?.effective_cr)}</td>
                        </tr>
                      </>
                    )}
                    {bidhistory?.history?.bid_stage?.startsWith("v2") && (
                      <>
                        <tr>
                          <th>Optimal Bid</th>
                          <td>{currencyFormatter(bidhistory?.history?.optimal_bid)}</td>
                        </tr>
                        <tr>
                          <th>CR Status</th>
                          <td>{bidhistory?.history?.cr_status}</td>
                        </tr>
                        <tr>
                          <th>End Bid</th>
                          <td>{currencyFormatter(bidhistory?.history?.end_bid)}</td>
                        </tr>
                      </>
                    )}
                    <tr>
                      <th>Target CR</th>
                      <td>{percentFromatter(bidhistory?.history?.kw_cr)}</td>``
                    </tr>
                    <tr>
                      <th>CR 30 days</th>
                      <td>{percentFromatter(bidhistory?.history?.cr_30d)}</td>
                    </tr>
                    <tr>
                      <th>CR 180 days</th>
                      <td>{percentFromatter(bidhistory?.history?.cr_180d)}</td>
                    </tr>
                  </tbody>
                </table>
              </Col>
              <Col xs={8}>
                <ImpressionCurve data={bidhistory?.exploitation_curves} />
              </Col>
              <Col xs={12} className="py-4">
                <Accordion title="debug">
                  {/* exclude the key exploitation_curves */}
                  <pre className="bg-light p-2">{JSON.stringify(details, null, 2)}</pre>
                </Accordion>
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    </ExpanderContainer>
  );
};

export default TargetDetails;
